import * as yup from 'yup';
import { DonationFrequency } from '../../../models/enums/DonationFrequency';
import { IAddress, IDonationSurvey, IDonorInfoCollectionOptions } from '../../../models/Api';

export const buildCustomPropertiesSchema = (
	fields: { id: string; isHidden?: boolean; isOptional?: boolean; label: string }[]
) => {
	return yup.object().shape(
		fields.reduce(
			(schema, field) => {
				if (field.isHidden) return schema;
				const errorMessage = `${field.label} is required`;
				schema[field.id] = field.isOptional ? yup.string().optional() : yup.string().required(errorMessage);
				return schema;
			},
			{} as Record<string, any>
		)
	);
};

export type IFormValues = {
	amount?: number;
	customAmount?: number;
	coverFees: boolean;
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	address?: IAddress;
	campaignId?: string;
	customProperties: Record<string, string>;
	donationFrequency?: DonationFrequency | null;
};

const buildDonorCollectionSchema = ({
	collectDonorPhone,
	collectDonorAddress,
}: {
	collectDonorPhone: IDonorInfoCollectionOptions;
	collectDonorAddress: IDonorInfoCollectionOptions;
}) => {
	return {
		firstName: yup.string().required(),
		lastName: yup.string().required(),
		email: yup.string().email('Invalid email').required('Email is required'),
		...(collectDonorPhone.require
			? { phone: yup.string().required('Phone is required') }
			: { phone: yup.string().optional() }),
		...(collectDonorAddress.require
			? {
					address: yup.object().shape({
						address1: yup.string().required('Address Line 1 is required'),
						address2: yup.string().optional(),
						city: yup.string().required('City is required'),
						stateProvince: yup.string().required('State/Province is required'),
						postalCode: yup.string().required('Postal Code is required'),
						country: yup.string().optional(),
					}),
				}
			: {
					address: yup.object().shape({
						address1: yup.string().optional(),
						address2: yup.string().optional(),
						city: yup.string().optional(),
						stateProvince: yup.string().optional(),
						postalCode: yup.string().optional(),
						country: yup.string().optional(),
					}),
				}),
	};
};

export const donationSurveyResponseValidationSchema = (survey: IDonationSurvey) =>
	yup.object().shape({
		amount: yup
			.number()
			.nullable()
			.test('amount-required', 'Please select or enter a donation amount', function (value) {
				const { customAmount } = this.parent;
				// At least one must be present
				return !!(value || customAmount);
			})
			.test('amount-exclusive', 'Please select a preset amount or enter a custom amount, not both', function (value) {
				const { customAmount } = this.parent;
				// Both cannot be present
				return !(value && customAmount);
			}),
		customAmount: yup
			.number()
			.nullable()
			.test('minimum-amount', function (value) {
				if (!value) return true; // If no custom amount, validation passes (handled by amount-required test)

				const { donationFrequency } = this.parent;
				const options = survey.donationOptions;

				// Determine which frequency settings to use
				let currentFrequencySettings;
				if (donationFrequency === DonationFrequency.Monthly) {
					currentFrequencySettings = options.monthlyDonationSettings || options.oneTimeDonationSettings || options;
				} else if (donationFrequency === DonationFrequency.Yearly) {
					currentFrequencySettings = options.annualDonationSettings || options.oneTimeDonationSettings || options;
				} else {
					currentFrequencySettings = options.oneTimeDonationSettings || options;
				}

				// Check if minimum amount is set and if custom amount is below it
				const minimumAmount = currentFrequencySettings.minimumAmount;
				if (minimumAmount && value < minimumAmount) {
					return this.createError({
						message: `Minimum donation amount is $${minimumAmount}`,
					});
				}

				return true;
			}),
		coverFees: yup.boolean().required('Cover fees is required'),
		campaignId: yup.string().optional(),
		donationFrequency: yup.string().nullable().optional(),
		customProperties: survey?.customForm?.fields
			? buildCustomPropertiesSchema(
					survey.customForm.fields.map(field => ({
						...field,
						id: field.id || '',
					}))
				)
			: yup.object().shape({}),
		...buildDonorCollectionSchema({
			collectDonorPhone: survey.donationOptions.collectDonorPhone,
			collectDonorAddress: survey.donationOptions.collectDonorAddress,
		}),
	});
